import React, { useState, useMemo } from "react";
import {
  Box, Typography, Button, Chip, Avatar, TextField, CircularProgress,
  Stack, Divider, alpha, IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetClassLiveDetailQuery, useStartClassLiveMutation, useEndClassLiveMutation,
  useMarkClassAttendanceMutation, useHandleClassRequestMutation, useSendClassMessageMutation,
  useProposeClassRequestAlternativeMutation, useScheduleClassRequestMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import dayjs from "dayjs";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { formatFromPrejoined } from "../../utils/classLabel";

const fmt12 = (t: string | undefined) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const StaffLiveClass = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading, refetch } = useGetClassLiveDetailQuery({ id: Number(id) }, { skip: !id, pollingInterval: 15000 });
  const [startClass, { isLoading: isStarting }] = useStartClassLiveMutation();
  const [endClass, { isLoading: isEnding }] = useEndClassLiveMutation();
  const [markAttendance] = useMarkClassAttendanceMutation();
  const [handleRequest] = useHandleClassRequestMutation();
  const [sendMessage, { isLoading: isSending }] = useSendClassMessageMutation();

  const detail = useMemo(() => (data?.data as any) || null, [data]);
  const schedule = detail?.schedule;
  const isLive = detail?.displayStatus === "live";
  const isCompleted = detail?.displayStatus === "completed";
  const [classNotes, setClassNotes] = useState("");
  const [msgText, setMsgText] = useState("");

  const handleStartClass = async () => {
    try {
      const r = await startClass({ id: Number(id) }).unwrap();
      if (r.status) { showMessage("Class started"); refetch(); }
      else showErrorMessage(r.message);
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const handleEndClass = async () => {
    try {
      const r = await endClass({ id: Number(id), notes: classNotes }).unwrap();
      if (r.status) { showMessage("Class ended"); refetch(); }
      else showErrorMessage(r.message);
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const handleAttendance = async (studentId: number, status: string) => {
    try {
      await markAttendance({ class_schedule_id: Number(id), student_id: studentId, status }).unwrap();
      showMessage(`Marked ${status}`);
      refetch();
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const handleReqAction = async (reqId: number, status: string) => {
    try {
      await handleRequest({ id: reqId, status }).unwrap();
      showMessage(`Request ${status}`);
      refetch();
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const handleSendMessage = async () => {
    if (!msgText.trim() || !schedule) return;
    try {
      await sendMessage({ class_schedule_id: schedule.id, sender_type: "staff", message: msgText.trim() }).unwrap();
      setMsgText("");
      refetch();
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  // Elapsed time (must be before early return)
  const elapsed = useMemo(() => {
    if (!schedule?.live_started_at) return "";
    const diff = dayjs().diff(dayjs(schedule.live_started_at), "minute");
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }, [schedule?.live_started_at]);

  if (isLoading || !detail) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress size={32} /></Box>;
  }

  const requests: any[] = detail.requests || [];
  const messages: any[] = detail.messages || [];
  const pendingRequests = requests.filter((r: any) => r.status === "pending");

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh", mx: -3, mt: -3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
        sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, mb: 2, color: "text.secondary" }}>
        Back
      </Button>

      {/* ══ Hero ══ */}
      <Box sx={{
        background: isLive ? "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)" : isCompleted ? "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)" : "linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%)",
        borderRadius: "16px", px: { xs: 2.5, md: 3.5 }, py: 2.5, mb: 3, color: "#fff",
        display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between",
        flexDirection: { xs: "column", md: "row" }, gap: 2,
      }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            {isLive && <FiberManualRecordIcon sx={{ fontSize: 12, color: "#fff", animation: "pulse 1.5s infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } } }} />}
            <Chip label={isLive ? `Live · ${elapsed}` : isCompleted ? "Completed" : "Not Started"} size="small"
              sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: "rgba(255,255,255,0.2)", color: "#fff" }} />
          </Box>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800 }}>
            {((detail as any).className ?? formatFromPrejoined((detail as any).name, detail.gradeName, detail.subjectName)) || "Class"}
          </Typography>
          <Typography sx={{ fontSize: 13, opacity: 0.8 }}>
            {dayjs(schedule?.scheduledDate).format("dddd, MMM D")} · {fmt12(schedule?.startTime)} – {fmt12(schedule?.endTime)}
            {detail.locationName ? ` · ${detail.locationName}` : ""}
          </Typography>
          {detail.staffName && (
            <Typography sx={{ fontSize: 12, opacity: 0.75, mt: 0.25 }}>
              Assigned Teacher: {detail.staffName}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1.5}>
          {!isLive && !isCompleted && (
            <Button variant="contained" startIcon={isStarting ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleStartClass} disabled={isStarting}
              sx={{ bgcolor: "#22c55e", textTransform: "none", fontWeight: 700, borderRadius: "12px", px: 3, "&:hover": { bgcolor: "#16a34a" } }}>
              Start Class
            </Button>
          )}
          {isLive && (
            <Button variant="contained" startIcon={isEnding ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
              onClick={handleEndClass} disabled={isEnding}
              sx={{ bgcolor: "rgba(255,255,255,0.2)", textTransform: "none", fontWeight: 700, borderRadius: "12px", px: 3, "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
              End Class
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 380px" }, gap: 3 }}>
        {/* ══ Left ══ */}
        <Stack spacing={3}>
          {/* Student Roster + Attendance */}
          <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(0,0,0,0.05)", p: 2.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2 }}>Student</Typography>
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              p: 1.5, borderRadius: "12px", border: "1px solid rgba(0,0,0,0.05)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }}>
                  {(detail.studentName || "S").charAt(0)}
                </Avatar>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{detail.studentName}</Typography>
              </Box>
              {(isLive || isUpcoming(detail)) && (
                <Stack direction="row" spacing={0.75}>
                  {["present", "late", "absent"].map((s) => (
                    <Chip key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} size="small"
                      onClick={() => handleAttendance(schedule.studentId, s)}
                      sx={{
                        height: 28, fontSize: 11, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
                        bgcolor: alpha(s === "present" ? "#22c55e" : s === "late" ? "#f59e0b" : "#ef4444", 0.06),
                        color: s === "present" ? "#16a34a" : s === "late" ? "#b45309" : "#dc2626",
                        border: `1px solid ${alpha(s === "present" ? "#22c55e" : s === "late" ? "#f59e0b" : "#ef4444", 0.15)}`,
                        "&:hover": { bgcolor: alpha(s === "present" ? "#22c55e" : s === "late" ? "#f59e0b" : "#ef4444", 0.12) },
                      }} />
                  ))}
                </Stack>
              )}
            </Box>
          </Box>

          {/* Pending Requests — Phase-6.1 upgraded: shows parent makeup
              preferences and adds Suggest Another Time / Approve & Schedule
              actions that delegate to the canonical scheduleMakeup path. */}
          {pendingRequests.length > 0 && (
            <StaffRequestsPanel requests={pendingRequests} onDone={() => refetch()} />
          )}

          {/* Class Notes */}
          {(isLive || isCompleted) && (
            <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(0,0,0,0.05)", p: 2.5 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1.5 }}>Class Notes</Typography>
              <TextField fullWidth multiline rows={4} placeholder="Add notes about this session..."
                value={classNotes || schedule?.class_notes || ""} onChange={(e) => setClassNotes(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", fontSize: 13 } }} />
            </Box>
          )}
        </Stack>

        {/* ══ Right: Messages ══ */}
        <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(0,0,0,0.05)", display: "flex", flexDirection: "column", height: "fit-content", maxHeight: 500 }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid rgba(0,0,0,0.04)", display: "flex", alignItems: "center", gap: 1 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 17, color: "#0ea5e9" }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Messages</Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2, minHeight: 150 }}>
            {messages.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ fontSize: 13, color: "text.disabled" }}>No messages yet</Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {messages.map((msg: any) => {
                  const isMe = msg.sender_type === "staff";
                  return (
                    <Box key={msg.id} sx={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start" }}>
                      <Box sx={{ maxWidth: "80%", bgcolor: isMe ? theme.palette.primary.main : "rgba(0,0,0,0.04)", color: isMe ? "#fff" : "text.primary", borderRadius: isMe ? "14px 14px 4px 14px" : "14px 14px 14px 4px", px: 2, py: 1.25 }}>
                        {!isMe && <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#0ea5e9", mb: 0.25 }}>{msg.senderName}</Typography>}
                        <Typography sx={{ fontSize: 13, lineHeight: 1.4 }}>{msg.message}</Typography>
                        <Typography sx={{ fontSize: 9, mt: 0.5, opacity: 0.6 }}>{dayjs(msg.created_at).format("h:mm A")}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid rgba(0,0,0,0.04)", display: "flex", gap: 1 }}>
            <TextField size="small" fullWidth placeholder="Message parent..."
              value={msgText} onChange={(e) => setMsgText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 13 } }} />
            <Button variant="contained" onClick={handleSendMessage} disabled={!msgText.trim() || isSending}
              sx={{ minWidth: 40, borderRadius: "10px", boxShadow: "none", px: 1.5 }}>
              {isSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Helper
const isUpcoming = (detail: any) => detail?.displayStatus === "upcoming" || detail?.displayStatus === "scheduled";

// ═══════════════════════════════════════════════════════════════════
//  Phase-6.1 Staff Requests Panel
//
//  Renders per-request parent context (reason, scheduling mode,
//  preferred slot / flexible preference) and the correct action set:
//    absence  → Approve / Reject
//    makeup   → Approve & Schedule / Suggest Another Time / Decline
//
//  All actions delegate to backend endpoints that reuse the canonical
//  scheduling helpers — this component never invents makeup creation
//  logic client-side.
// ═══════════════════════════════════════════════════════════════════

const StaffRequestsPanel: React.FC<{ requests: any[]; onDone: () => void }> = ({ requests, onDone }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [handleReq, { isLoading: handling }] = useHandleClassRequestMutation();
  const [propose, { isLoading: proposing }] = useProposeClassRequestAlternativeMutation();
  const [schedule, { isLoading: scheduling }] = useScheduleClassRequestMutation();
  const [proposeFor, setProposeFor] = useState<any>(null);
  const [pDate, setPDate] = useState("");
  const [pStart, setPStart] = useState("");
  const [pEnd, setPEnd] = useState("");
  const [pStaff, setPStaff] = useState("");
  const [pNote, setPNote] = useState("");

  const resetProposal = () => { setPDate(""); setPStart(""); setPEnd(""); setPStaff(""); setPNote(""); setProposeFor(null); };

  const act = async (fn: () => Promise<any>, okMsg?: string) => {
    try {
      const r = await fn();
      if (r.status) { showMessage(okMsg || r.message); onDone(); return true; }
      showErrorMessage(r.message);
    } catch (e: any) { showErrorMessage(e?.data?.message || "Action failed"); }
    return false;
  };

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(0,0,0,0.05)", p: 2.5 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2 }}>Parent Requests ({requests.length})</Typography>
      <Stack spacing={1.25}>
        {requests.map((req: any) => {
          const isMakeup = req.request_type === "makeup";
          const flexible = req.scheduling_mode === "flexible";
          return (
            <Box key={req.id} sx={{ p: 1.5, borderRadius: "12px", border: `1px solid ${alpha("#f59e0b", 0.18)}`, bgcolor: alpha("#f59e0b", 0.03) }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  {isMakeup ? "Makeup" : req.request_type === "absent" ? "Absence" : req.request_type} Request
                </Typography>
                {req.student_name && <Chip label={req.student_name} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />}
                <Chip label={req.status} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, textTransform: "capitalize" }} />
              </Box>
              {req.reason && <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>Reason: {req.reason}</Typography>}

              {isMakeup && !flexible && req.requested_date && (
                <Box sx={{ mt: 0.75, pl: 1, borderLeft: `2px solid ${alpha("#f59e0b", 0.4)}` }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>Parent Preference</Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    {dayjs(req.requested_date).format("ddd, MMM D, YYYY")} · {fmt12(req.requested_time)} – {fmt12(req.requested_end_time)}
                    {req.preferred_staff_name ? ` · ${req.preferred_staff_name}` : ""}
                  </Typography>
                </Box>
              )}
              {isMakeup && flexible && (
                <Box sx={{ mt: 0.75, pl: 1, borderLeft: `2px solid ${alpha("#f59e0b", 0.4)}` }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>Parent Preference</Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    Flexible
                    {Array.isArray(req.preferred_days) && req.preferred_days.length > 0 ? ` · Days: ${req.preferred_days.join(", ")}` : ""}
                    {req.preferred_time_of_day && req.preferred_time_of_day !== "any" ? ` · ${req.preferred_time_of_day}` : ""}
                  </Typography>
                </Box>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap", gap: 0.75 }}>
                {isMakeup ? (
                  <>
                    <Button
                      size="small" variant="contained" disabled={handling || scheduling}
                      onClick={() => act(() => schedule({ id: req.id }).unwrap(), "Makeup scheduled.")}
                      sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700, bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}
                    >
                      Approve & Schedule
                    </Button>
                    <Button
                      size="small" variant="outlined" disabled={handling || proposing}
                      onClick={() => { setProposeFor(req); setPDate(req.requested_date || ""); setPStart(req.requested_time || ""); setPEnd(req.requested_end_time || ""); setPStaff(req.preferred_staff_id ? String(req.preferred_staff_id) : ""); }}
                      sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700 }}
                    >
                      Suggest Another Time
                    </Button>
                    <Button
                      size="small" variant="outlined" disabled={handling}
                      onClick={() => act(() => handleReq({ id: req.id, status: "rejected" }).unwrap(), "Request declined.")}
                      sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700, color: "#dc2626", borderColor: alpha("#ef4444", 0.4) }}
                    >
                      Decline
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="small" variant="contained" disabled={handling}
                      onClick={() => act(() => handleReq({ id: req.id, status: "approved" }).unwrap(), "Request approved.")}
                      sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700, bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small" variant="outlined" disabled={handling}
                      onClick={() => act(() => handleReq({ id: req.id, status: "rejected" }).unwrap(), "Request declined.")}
                      sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700, color: "#dc2626", borderColor: alpha("#ef4444", 0.4) }}
                    >
                      Reject
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {/* Suggest Another Time inline form (rendered under the panel to
          keep this component simple — full dialog can come later). */}
      {proposeFor && (
        <Box sx={{ mt: 2, p: 1.75, borderRadius: "12px", border: `1px solid ${alpha("#8b5cf6", 0.2)}`, bgcolor: alpha("#8b5cf6", 0.04) }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Suggest Another Time (request #{proposeFor.id})</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1, mb: 1 }}>
            <TextField label="Proposed Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={pDate} onChange={(e) => setPDate(e.target.value)} />
            <TextField label="Start" type="time" size="small" InputLabelProps={{ shrink: true }} value={pStart} onChange={(e) => setPStart(e.target.value)} />
            <TextField label="End" type="time" size="small" InputLabelProps={{ shrink: true }} value={pEnd} onChange={(e) => setPEnd(e.target.value)} />
            <TextField label="Teacher ID" type="number" size="small" value={pStaff} onChange={(e) => setPStaff(e.target.value)} />
            <TextField label="Note (optional)" size="small" value={pNote} onChange={(e) => setPNote(e.target.value)} sx={{ gridColumn: { xs: "1", sm: "1 / span 3" } }} />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              size="small" variant="contained" disabled={proposing || !pDate || !pStart || !pEnd || !pStaff}
              onClick={async () => {
                const ok = await act(() => propose({
                  id: proposeFor.id,
                  proposed_date: pDate,
                  proposed_start_time: pStart,
                  proposed_end_time: pEnd,
                  proposed_staff_id: Number(pStaff),
                  proposal_note: pNote || undefined,
                }).unwrap(), "Alternative proposed.");
                if (ok) resetProposal();
              }}
              sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700 }}
            >
              Send Proposal to Parent
            </Button>
            <Button size="small" onClick={resetProposal} sx={{ textTransform: "none", fontSize: 11.5 }}>Cancel</Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default StaffLiveClass;
